import { clearAuthToken } from "../common/utils/tokenStorage";
import { isSuperAdminUser } from "../common/utils/jwt";
import { getSelectedRestaurant } from "../common/utils/restaurantContext";
import { API_BASE_URL } from './config';
import { ensureCsrfToken, getCachedCsrfToken } from "../common/utils/csrf";


type ApiRequestOptions = Omit<RequestInit, "headers"> & {
  headers?: Record<string, string>;
};

type StandardError = {
  retryAfter?: number;
  code?: string;
  message?: string;
  details?: unknown;
};

type StandardResponse<T = unknown> = {
  retryAfter?: number;
  success?: boolean;
  data?: T;
  message?: string;
  error?: StandardError;
};

export type ApiError = Error & {
  retryAfter?: number | undefined;
  code?: string;
  details?: unknown;
  statusCode?: number;
};

export async function apiRequest<T = unknown>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const config: ApiRequestOptions = {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...(options.headers || {}),
    },
  };

  const restaurant = getSelectedRestaurant();
  if (isSuperAdminUser() && restaurant && !config.headers?.['X-Restaurant-Id'] && !endpoint.startsWith('/api/auth/') && !endpoint.startsWith('/api/super-admin/') && endpoint !== '/api/restaurants') {
    config.headers = { ...config.headers, 'X-Restaurant-Id': restaurant };
  }


  const method = (config.method || "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
    const csrfToken = getCachedCsrfToken() || (await ensureCsrfToken(config.signal ?? undefined));
    config.headers = {
      ...(config.headers || {}),
      "X-CSRF-Token": csrfToken,
    };
  }

  const response = await fetch(url, config);
  const data = (await response
    .json()
    .catch(
      () => ({ success: false, error: { message: "Invalid response format" } }) as StandardResponse
    )) as StandardResponse<T>;

  if (!response.ok) {
    const retryHeader = response.headers?.get('Retry-After');
    const headerSeconds = retryHeader && /^\d+$/.test(retryHeader.trim())
      ? Number(retryHeader) : retryHeader ? Math.ceil((Date.parse(retryHeader) - Date.now()) / 1000) : NaN;
    const retrySeconds = Number.isFinite(headerSeconds) ? headerSeconds : data.error?.retryAfter ?? data.retryAfter;
    const retryAfter = typeof retrySeconds === 'number' && Number.isFinite(retrySeconds) && retrySeconds > 0 ? Math.ceil(retrySeconds) : undefined;
    if (response.status === 401) clearAuthToken();
    if (data.error) {
      const error = new Error(data.error.message || "Request failed") as ApiError;
      if (data.error.code !== undefined) {
        error.code = data.error.code;
      }
      if (data.error.details !== undefined) {
        error.details = data.error.details;
      }
      error.statusCode = response.status;
      error.retryAfter = retryAfter;
      throw error;
    }

    const error = new Error(data.message || `HTTP error! status: ${response.status}`) as ApiError;
    error.statusCode = response.status;
    error.retryAfter = retryAfter;
    throw error;
  }

  if (data.success && data.data !== undefined) {
    return data.data;
  }

  return data as T;
}

export async function get<T = unknown>(endpoint: string, options: ApiRequestOptions = {}) {
  return apiRequest<T>(endpoint, { ...options, method: "GET" });
}

export async function post<T = unknown>(
  endpoint: string,
  data: unknown,
  options: ApiRequestOptions = {}
) {
  return apiRequest<T>(endpoint, {
    ...options,
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function put<T = unknown>(
  endpoint: string,
  data: unknown,
  options: ApiRequestOptions = {}
) {
  return apiRequest<T>(endpoint, {
    ...options,
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function del<T = unknown>(endpoint: string, options: ApiRequestOptions = {}) {
  return apiRequest<T>(endpoint, { ...options, method: "DELETE" });
}
